import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CATEGORIES } from '@/data/categories';
import { ImageCropper } from '@/components/ImageCropper';
import PotentialMatchesTab from '@/components/PotentialMatchesTab';
import AdminTicketsTab from '@/components/admin/AdminTicketsTab';
import AdminPresenceTab from '@/components/admin/AdminPresenceTab';
import AdminSystemHealth from '@/components/admin/AdminSystemHealth';
import AdminChatViolationsPanel from '@/components/admin/AdminChatViolationsPanel';
import AdminChatMonitorPanel from '@/components/admin/AdminChatMonitorPanel';
import { AdminStatsPanel } from '@/components/admin/AdminStatsPanel';
import { ChartsDashboard, RecapDashboard } from '@/components/admin/AnalyticsDashboard';
import { UserManagementTable, AccountsTable } from '@/components/admin/UserManagementTable';
import { MemberFormFields } from '@/components/admin/MemberFormFields';
import { Users, Heart, LogOut, Shield, Plus, Camera, Key, Sun, Moon, UserCheck, AlertTriangle, ChevronRight, ChevronLeft, Search, Sparkles, X, LifeBuoy, Activity, ShieldAlert } from 'lucide-react';

const INITIAL_CREATE_FORM = {
  gender: '', category: '', age: '', domicile: '', country: '', city: '', location_detail: '', education: '',
  occupation: '', whatsapp: '', instagram: '', tiktok: '', facebook: '',
  bio: '', shio: '', mbti: '', zodiac: '', preferred_places: '',
  aliran_agama: '', hobby: '', topics_to_talk: '', marriage_status: '',
  tujuan_join: '', kriteria_pasangan: '', income_range: '',
};
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { token, logout, API } = useAuth();
  const { t, lang } = useLang();
  const { theme, toggleTheme } = useTheme();
  const [members, setMembers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [createForm, setCreateForm] = useState(INITIAL_CREATE_FORM);
  const [createPhotos, setCreatePhotos] = useState(['', '', '']);
  const [passkey, setPasskey] = useState('');
  const [newPasskey, setNewPasskey] = useState('');
  const [passkeyUpdatedAt, setPasskeyUpdatedAt] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedCatDetail, setSelectedCatDetail] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropTarget, setCropTarget] = useState(null); // {mode: 'create'|'edit', index: 0|1|2}

  // Search + pagination + filter + sort for Member Profiles tab
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const [filterCategory, setFilterCategory] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const PAGE_SIZE = 20;

  // Debounce search query (300ms)
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(id);
  }, [searchQuery]);

  // Reset to page 1 whenever the search term / filter / sort changes
  useEffect(() => { setPage(1); }, [debouncedQuery, filterCategory, sortBy, sortOrder]);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchMembers = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (debouncedQuery) params.append('q', debouncedQuery);
      if (filterCategory) params.append('category', filterCategory);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      const res = await axios.get(`${API}/admin/members?${params.toString()}`, { headers });
      setMembers(res.data.members || []);
      setTotalMembers(res.data.total || 0);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) { logout(); navigate('/admin'); }
    }
  }, [API, headers, logout, navigate, page, debouncedQuery, filterCategory, sortBy, sortOrder]);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, passkeyRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/passkey`, { headers }),
      ]);
      setStats(statsRes.data.stats || null);
      setPasskey(passkeyRes.data.passkey || '');
      setPasskeyUpdatedAt(passkeyRes.data.updated_at || '');
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        logout();
        navigate('/admin');
      } else {
        toast.error(lang === 'en' ? 'Failed to load stats — check connection' : 'Gagal memuat statistik — cek koneksi');
      }
    }
    setLoading(false);
  }, [API, headers, logout, navigate, lang]);

  // ==== Akun Anggota (Member Accounts) — pagination + filter + sort ====
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const [accountDebouncedQuery, setAccountDebouncedQuery] = useState('');
  const [accountPage, setAccountPage] = useState(1);
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [accountClaimStatus, setAccountClaimStatus] = useState('all'); // all | claimed | unclaimed
  const [accountClaimFrom, setAccountClaimFrom] = useState('');        // yyyy-mm-dd (native date input)
  const [accountClaimTo, setAccountClaimTo] = useState('');            // yyyy-mm-dd
  const [accountSortBy, setAccountSortBy] = useState('claimed_at');    // claimed_at | created_at | name | whatsapp
  const [accountSortOrder, setAccountSortOrder] = useState('desc');    // desc | asc
  const ACCOUNTS_PAGE_SIZE = 25;

  useEffect(() => {
    const id = setTimeout(() => setAccountDebouncedQuery(accountSearchQuery.trim()), 300);
    return () => clearTimeout(id);
  }, [accountSearchQuery]);

  useEffect(() => { setAccountPage(1); }, [accountDebouncedQuery, accountClaimStatus, accountClaimFrom, accountClaimTo, accountSortBy, accountSortOrder]);

  const fetchAccounts = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: String(accountPage),
        limit: String(ACCOUNTS_PAGE_SIZE),
        claim_status: accountClaimStatus,
        sort_by: accountSortBy,
        sort_order: accountSortOrder,
      });
      if (accountDebouncedQuery) params.append('q', accountDebouncedQuery);
      if (accountClaimFrom) params.append('claim_from', accountClaimFrom);
      if (accountClaimTo) params.append('claim_to', accountClaimTo);
      const res = await axios.get(`${API}/admin/accounts?${params.toString()}`, { headers });
      setAccounts(res.data.accounts || []);
      setTotalAccounts(res.data.total || 0);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) { logout(); navigate('/admin'); }
    }
  }, [API, headers, logout, navigate, accountPage, accountDebouncedQuery, accountClaimStatus, accountClaimFrom, accountClaimTo, accountSortBy, accountSortOrder]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const clearAccountFilters = () => {
    setAccountSearchQuery('');
    setAccountClaimStatus('all');
    setAccountClaimFrom('');
    setAccountClaimTo('');
    setAccountSortBy('claimed_at');
    setAccountSortOrder('desc');
  };

  const [unclaimAccountTarget, setUnclaimAccountTarget] = useState(null);
  const [unclaimingAccount, setUnclaimingAccount] = useState(false);
  const handleUnclaimAccount = async () => {
    if (!unclaimAccountTarget) return;
    setUnclaimingAccount(true);
    try {
      await axios.post(`${API}/admin/accounts/${unclaimAccountTarget.id}/unclaim`, {}, { headers });
      toast.success(lang === 'en' ? 'Claim reset — profile now free to re-claim' : 'Klaim direset — profil bisa diklaim ulang');
      setUnclaimAccountTarget(null);
      fetchAccounts();
      fetchData();
      fetchMembers();
    } catch (err) {
      toast.error(lang === 'en' ? 'Failed to reset claim' : 'Gagal reset klaim');
    }
    setUnclaimingAccount(false);
  };

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  // Auto-refresh stats/members/accounts when the tab regains focus so admin sees latest counts
  useEffect(() => {
    const onFocus = () => { fetchData(); fetchMembers(); fetchAccounts(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchData, fetchMembers, fetchAccounts]);

  const refreshAll = useCallback(() => {
    fetchData();
    fetchMembers();
    fetchAccounts();
    toast.success(lang === 'en' ? 'Refreshed' : 'Data disegarkan');
  }, [fetchData, fetchMembers, fetchAccounts, lang]);

  const handleCreate = async () => {
    const hasCountry = (createForm.country || '').trim();
    if (!createForm.gender || !createForm.category || !createForm.age || !hasCountry) {
      toast.error(lang === 'en' ? 'Fill required fields (gender, category, age, country)' : 'Isi kolom wajib (gender, kategori, usia, negara)');
      return;
    }
    try {
      await axios.post(`${API}/admin/members`, {
        ...createForm,
        age: parseInt(createForm.age),
        photos: createPhotos
      }, { headers });
      toast.success(lang === 'en' ? 'Member created!' : 'Anggota dibuat!');
      setShowCreate(false);
      setCreateForm(INITIAL_CREATE_FORM);
      setCreatePhotos(['', '', '']);
      fetchData();
      fetchMembers();
    } catch (err) { toast.error('Failed'); }
  };

  const openCropForCreate = (index) => {
    setCropTarget({ mode: 'create', index });
    setCropOpen(true);
  };

  const openCropForEdit = (index) => {
    setCropTarget({ mode: 'edit', index });
    setCropOpen(true);
  };

  const handleCropComplete = async (base64) => {
    if (!cropTarget) return;
    if (cropTarget.mode === 'create') {
      const newPhotos = [...createPhotos];
      newPhotos[cropTarget.index] = base64;
      setCreatePhotos(newPhotos);
    } else if (cropTarget.mode === 'edit' && editMember) {
      try {
        await axios.post(`${API}/admin/members/photo`, { member_id: editMember.id, photo: base64, index: cropTarget.index }, { headers });
        toast.success('Photo updated');
        const updatedPhotos = [...(editMember.photos || ['', '', ''])];
        updatedPhotos[cropTarget.index] = base64;
        setEditMember({ ...editMember, photos: updatedPhotos });
        fetchMembers();
      } catch { toast.error('Failed to upload photo'); }
    }
    setCropTarget(null);
  };

  const handleToggleMatch = async (memberId) => {
    try {
      const res = await axios.put(`${API}/admin/members/${memberId}/match`, {}, { headers });
      toast.success(res.data.is_matched ? 'Marked as Matched!' : 'Unmarked');
      fetchMembers();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/members/${deleteTarget.id}`, { headers });
      toast.success(lang === 'en' ? 'Member deleted' : 'Anggota dihapus');
      setDeleteTarget(null);
      fetchData();
      fetchMembers();
    } catch (err) {
      toast.error(lang === 'en' ? 'Failed to delete member' : 'Gagal menghapus anggota');
    }
    setDeleting(false);
  };

  const [unclaimTarget, setUnclaimTarget] = useState(null);
  const [unclaiming, setUnclaiming] = useState(false);
  const handleUnclaim = async () => {
    if (!unclaimTarget) return;
    setUnclaiming(true);
    try {
      await axios.post(`${API}/admin/members/${unclaimTarget.id}/unclaim`, {}, { headers });
      toast.success(lang === 'en' ? 'Claim reset successfully' : 'Klaim berhasil direset');
      setUnclaimTarget(null);
      fetchData();
      fetchMembers();
    } catch (err) {
      toast.error(lang === 'en' ? 'Failed to reset claim' : 'Gagal reset klaim');
    }
    setUnclaiming(false);
  };

  const handleEdit = (member) => {
    setEditMember(member);
    setEditForm({
      name: member.name || '', age: member.age?.toString() || '', domicile: member.domicile || '',
      country: member.country || '', city: member.city || '', location_detail: member.location_detail || '',
      education: member.education || '', occupation: member.occupation || '', whatsapp: member.whatsapp || '',
      instagram: member.instagram || '', tiktok: member.tiktok || '', facebook: member.facebook || '',
      category: member.category || '', gender: member.gender || '', bio: member.bio || '',
      shio: member.shio || '', mbti: member.mbti || '', zodiac: member.zodiac || '',
      preferred_places: member.preferred_places || '', aliran_agama: member.aliran_agama || '',
      hobby: member.hobby || '', topics_to_talk: member.topics_to_talk || '',
      marriage_status: member.marriage_status || '', tujuan_join: member.tujuan_join || '',
      kriteria_pasangan: member.kriteria_pasangan || '', income_range: member.income_range || '',
    });
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/admin/members/${editMember.id}`, { ...editForm, age: parseInt(editForm.age) || editMember.age }, { headers });
      toast.success('Updated');
      setEditMember(null);
      fetchMembers();
    } catch { toast.error('Failed'); }
  };

  const handleUpdatePasskey = async () => {
    if (!newPasskey.trim()) return;
    try {
      await axios.put(`${API}/admin/passkey`, { passkey: newPasskey.trim() }, { headers });
      toast.success(lang === 'en' ? 'Passkey updated!' : 'Kunci diperbarui!');
      setNewPasskey('');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleLogout = () => { logout(); navigate('/'); };
  const inputCls = "t-bg-input t-border h-10 rounded-lg text-sm px-4 t-text";

  return (
    <div className="min-h-screen t-bg">
      <div className="glass border-b px-6 py-4 flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-[#C7867A]" />
          <h1 className="text-lg font-bold t-text">Admin {t('dashboard')}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => navigate('/admin/buddhist')} variant="outline" className="rounded-full text-sm" data-testid="admin-buddhist-link">
            <Sparkles className="w-4 h-4 mr-1" />Buddhist Suite
          </Button>
          <button onClick={toggleTheme} data-testid="admin-theme-toggle" className="p-2 rounded-full glass t-text-secondary">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <Button onClick={handleLogout} variant="ghost" className="text-red-500 hover:text-red-400 hover:bg-red-500/10 rounded-full text-sm" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-1" />{t('logout')}
          </Button>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#C7867A] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <>
            {/* Tabs — every admin panel now lives in its own tab */}
            <Tabs defaultValue="overview" className="w-full">
              <div className="flex items-center justify-between mb-6 gap-2 flex-wrap">
                <TabsList className="glass flex-wrap h-auto">
                  <TabsTrigger value="overview" className="data-[state=active]:bg-[#D98A94] data-[state=active]:text-white t-text-secondary" data-testid="tab-overview">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />{lang === 'en' ? 'Overview' : 'Ringkasan'}
                  </TabsTrigger>
                  <TabsTrigger value="others" className="data-[state=active]:bg-[#6B7280] data-[state=active]:text-white t-text-secondary" data-testid="tab-others">
                    <Activity className="w-3.5 h-3.5 mr-1.5" />{lang === 'en' ? 'Others' : 'Lainnya'}
                  </TabsTrigger>
                  <TabsTrigger value="members" className="data-[state=active]:bg-[#C7867A] data-[state=active]:text-white t-text-secondary" data-testid="tab-members">
                    <Users className="w-4 h-4 mr-1.5" />{t('members')}
                  </TabsTrigger>
                  <TabsTrigger value="accounts" className="data-[state=active]:bg-[#C7867A] data-[state=active]:text-white t-text-secondary" data-testid="tab-accounts">
                    <UserCheck className="w-4 h-4 mr-1.5" />{t('accounts')}
                  </TabsTrigger>
                  <TabsTrigger value="ai-matches" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black t-text-secondary" data-testid="tab-ai-matches">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />AI Match
                  </TabsTrigger>
                  <TabsTrigger value="tickets" className="data-[state=active]:bg-[#D98A94] data-[state=active]:text-white t-text-secondary" data-testid="tab-tickets">
                    <LifeBuoy className="w-3.5 h-3.5 mr-1.5" />{lang === 'en' ? 'Tickets' : 'Tiket'}
                  </TabsTrigger>
                  <TabsTrigger value="activity" className="data-[state=active]:bg-green-600 data-[state=active]:text-white t-text-secondary" data-testid="tab-activity">
                    <Activity className="w-3.5 h-3.5 mr-1.5" />{lang === 'en' ? 'Activity' : 'Aktivitas'}
                  </TabsTrigger>
                </TabsList>
                <Button onClick={() => setShowCreate(true)} data-testid="create-member-btn" className="bg-[#C7867A] hover:bg-[#C7867A]/90 text-white rounded-full">
                  <Plus className="w-4 h-4 mr-1" />{t('create_member')}
                </Button>
              </div>

              {/* Overview: stats + charts + category guide + recap */}
              <TabsContent value="overview">
                <div className="flex items-center justify-between mb-3">
                  <span className="t-text-muted text-xs">
                    {lang === 'en' ? 'Live overview — auto-refreshes when tab regains focus.' : 'Ringkasan langsung — otomatis disegarkan saat tab difokuskan.'}
                  </span>
                  <Button onClick={refreshAll} variant="outline" size="sm" className="rounded-full text-xs" data-testid="admin-refresh-overview">
                    <Activity className="w-3.5 h-3.5 mr-1" />{lang === 'en' ? 'Refresh' : 'Segarkan'}
                  </Button>
                </div>
                <AdminStatsPanel stats={stats} t={t} />
                <ChartsDashboard stats={stats} lang={lang} />
                <div className="mt-8">
                  <h3 className="t-text font-semibold text-sm mb-4 flex items-center gap-2">
                    <Heart className="w-4 h-4 text-[#D98A94]" />
                    {lang === 'en' ? 'Match Categories Guide' : 'Panduan Kategori Match'}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {CATEGORIES.map(cat => (
                      <button key={cat.name} onClick={() => setSelectedCatDetail(cat)} data-testid={`cat-detail-${cat.name.replace(/\s/g, '-')}`}
                        className="glass-card rounded-xl p-3 text-left hover:scale-[1.02] hover:shadow-md transition-all group cursor-pointer" style={{ borderLeft: `3px solid ${cat.color}` }}>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                          <span className="t-text text-xs font-semibold group-hover:text-[#D98A94] transition-colors">{cat.name}</span>
                          <ChevronRight className="w-3 h-3 t-text-muted ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                        </div>
                        <p className="t-text-muted text-[10px] line-clamp-2 leading-relaxed">{lang === 'en' ? cat.en : cat.id}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <RecapDashboard stats={stats} lang={lang} />
              </TabsContent>

              {/* Others — merged: Server Health + Chat Monitor + Chat Violations + Site Passkey */}
              <TabsContent value="others">
                <div className="space-y-4">
                  <AdminSystemHealth API={API} token={token} lang={lang} />
                  <AdminChatMonitorPanel API={API} token={token} lang={lang} />
                  <AdminChatViolationsPanel API={API} token={token} lang={lang} />
                  <div className="glass-card rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Key className="w-5 h-5 text-[#F59E0B]" />
                      <h3 className="t-text font-semibold">{t('manage_passkey')}</h3>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                      <div>
                        <p className="t-text-muted text-xs mb-1">{t('current_passkey')}</p>
                        <p className="t-text text-lg font-mono font-bold" data-testid="current-passkey">{passkey}</p>
                        {passkeyUpdatedAt && <p className="t-text-muted text-xs mt-1">{lang === 'en' ? 'Last updated:' : 'Terakhir diperbarui:'} {(() => { const d=new Date(passkeyUpdatedAt); if(isNaN(d.getTime())) return '-'; return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}</p>}
                      </div>
                      <div className="flex gap-2 flex-1 sm:ml-auto">
                        <Input data-testid="new-passkey-input" value={newPasskey} onChange={e => setNewPasskey(e.target.value)} className={`${inputCls} flex-1`} placeholder={lang === 'en' ? 'New passkey...' : 'Kunci baru...'} />
                        <Button onClick={handleUpdatePasskey} data-testid="update-passkey-btn" className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-black rounded-lg font-medium">{t('update_passkey')}</Button>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Input Profil Anggota — CTA now merged into Akun Anggota tab */}
              <TabsContent value="members">
                {/* Search + filter + sort bar */}
                <div className="mb-3 flex items-center gap-2 flex-wrap">
                  <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 t-text-muted pointer-events-none" />
                    <Input
                      data-testid="admin-members-search"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder={lang === 'en' ? 'Search by name, WhatsApp or Instagram...' : 'Cari berdasarkan nama, WhatsApp atau Instagram...'}
                      className="t-bg-input t-border h-10 rounded-lg pl-9 text-sm t-text"
                    />
                  </div>
                  <select
                    data-testid="admin-filter-category"
                    value={filterCategory}
                    onChange={e => setFilterCategory(e.target.value)}
                    className="t-bg-input t-border h-10 rounded-lg text-sm t-text px-3 min-w-[160px]"
                    aria-label={lang === 'en' ? 'Filter by category' : 'Filter berdasarkan match'}
                  >
                    <option value="">{lang === 'en' ? 'All Categories' : 'Semua Match'}</option>
                    {CATEGORIES.map(c => (
                      <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                  <select
                    data-testid="admin-sort-by"
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    className="t-bg-input t-border h-10 rounded-lg text-sm t-text px-3"
                    aria-label={lang === 'en' ? 'Sort by' : 'Urut berdasarkan'}
                  >
                    <option value="created_at">{lang === 'en' ? 'Sort: Input Time' : 'Urut: Waktu Input'}</option>
                    <option value="name">{lang === 'en' ? 'Sort: Name' : 'Urut: Nama'}</option>
                    <option value="birth_year">{lang === 'en' ? 'Sort: Age' : 'Urut: Umur'}</option>
                  </select>
                  <button
                    data-testid="admin-sort-order-toggle"
                    onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
                    className="t-bg-input t-border h-10 px-3 rounded-lg text-sm t-text hover:bg-[#C7867A]/10 transition"
                    title={lang === 'en' ? 'Toggle sort direction' : 'Ubah arah urutan'}
                  >
                    {sortOrder === 'asc' ? (lang === 'en' ? '↑ Asc' : '↑ Naik') : (lang === 'en' ? '↓ Desc' : '↓ Turun')}
                  </button>
                  <span className="text-xs t-text-muted whitespace-nowrap ml-auto">
                    {lang === 'en'
                      ? `${totalMembers} member${totalMembers === 1 ? '' : 's'} total`
                      : `Total ${totalMembers} anggota`}
                  </span>
                </div>

                <UserManagementTable members={members} lang={lang} t={t} onEdit={handleEdit} onDelete={setDeleteTarget} onToggleMatch={handleToggleMatch} onUnclaim={setUnclaimTarget} />

                {/* Pagination */}
                {totalMembers > PAGE_SIZE && (
                  <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
                    <span className="text-xs t-text-muted">
                      {lang === 'en'
                        ? `Page ${page} of ${Math.max(1, Math.ceil(totalMembers / PAGE_SIZE))} - showing ${members.length} of ${totalMembers}`
                        : `Halaman ${page} dari ${Math.max(1, Math.ceil(totalMembers / PAGE_SIZE))} - menampilkan ${members.length} dari ${totalMembers}`}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        variant="outline"
                        size="sm"
                        data-testid="admin-members-prev"
                        className="t-border t-text rounded-full disabled:opacity-40"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />{lang === 'en' ? 'Prev' : 'Sebelumnya'}
                      </Button>
                      <Button
                        onClick={() => setPage(p => p + 1)}
                        disabled={page >= Math.ceil(totalMembers / PAGE_SIZE)}
                        variant="outline"
                        size="sm"
                        data-testid="admin-members-next"
                        className="t-border t-text rounded-full disabled:opacity-40"
                      >
                        {lang === 'en' ? 'Next' : 'Berikutnya'}<ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="accounts">
                {/* Input Profil Anggota — merged CTA */}
                <div className="glass-card rounded-2xl p-5 mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="input-profile-panel">
                  <div className="w-12 h-12 rounded-full bg-[#C7867A]/15 flex items-center justify-center flex-shrink-0">
                    <Plus className="w-6 h-6 text-[#C7867A]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="t-text text-sm font-bold mb-0.5">{lang === 'en' ? 'Add a New Member Profile' : 'Input Profil Anggota Baru'}</h3>
                    <p className="t-text-muted text-xs">
                      {lang === 'en'
                        ? 'Open the member creation form. Required: gender, category, age, and at least one location field.'
                        : 'Buka form input anggota. Wajib: gender, kategori, usia, dan minimal salah satu lokasi.'}
                    </p>
                  </div>
                  <Button onClick={() => setShowCreate(true)} data-testid="input-profile-open-btn" className="bg-[#C7867A] hover:bg-[#C7867A]/90 text-white rounded-full px-5 h-10">
                    <Plus className="w-4 h-4 mr-1.5" />{t('create_member')}
                  </Button>
                </div>

                {/* Filters + sort row — helps admin quickly find wrongly-claimed profiles */}
                <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="accounts-filter-bar">
                  <div className="relative flex-1 min-w-[220px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 t-text-muted pointer-events-none" />
                    <Input
                      data-testid="admin-accounts-search"
                      value={accountSearchQuery}
                      onChange={e => setAccountSearchQuery(e.target.value)}
                      placeholder={lang === 'en' ? 'Search WhatsApp / email / name...' : 'Cari WhatsApp / email / nama...'}
                      className="t-bg-input t-border h-10 rounded-lg pl-9 text-sm t-text"
                    />
                  </div>
                  <select
                    data-testid="admin-accounts-claim-status"
                    value={accountClaimStatus}
                    onChange={e => setAccountClaimStatus(e.target.value)}
                    className="t-bg-input t-border h-10 rounded-lg text-sm t-text px-3"
                    aria-label={lang === 'en' ? 'Claim status' : 'Status klaim'}
                  >
                    <option value="all">{lang === 'en' ? 'All accounts' : 'Semua akun'}</option>
                    <option value="claimed">{lang === 'en' ? 'Claimed only' : 'Sudah klaim'}</option>
                    <option value="unclaimed">{lang === 'en' ? 'Not claimed' : 'Belum klaim'}</option>
                  </select>
                  <div className="flex items-center gap-1.5" title={lang === 'en' ? 'Claim date range' : 'Rentang tanggal klaim'}>
                    <span className="text-xs t-text-muted whitespace-nowrap">{lang === 'en' ? 'Claim:' : 'Klaim:'}</span>
                    <Input
                      type="date"
                      value={accountClaimFrom}
                      onChange={e => setAccountClaimFrom(e.target.value)}
                      className="t-bg-input t-border h-10 rounded-lg text-sm t-text w-[145px] px-2"
                      data-testid="admin-accounts-claim-from"
                      aria-label={lang === 'en' ? 'Claim date from' : 'Tanggal klaim dari'}
                    />
                    <span className="t-text-muted text-xs">–</span>
                    <Input
                      type="date"
                      value={accountClaimTo}
                      onChange={e => setAccountClaimTo(e.target.value)}
                      className="t-bg-input t-border h-10 rounded-lg text-sm t-text w-[145px] px-2"
                      data-testid="admin-accounts-claim-to"
                      aria-label={lang === 'en' ? 'Claim date to' : 'Tanggal klaim sampai'}
                    />
                  </div>
                  <select
                    data-testid="admin-accounts-sort-by"
                    value={accountSortBy}
                    onChange={e => setAccountSortBy(e.target.value)}
                    className="t-bg-input t-border h-10 rounded-lg text-sm t-text px-3"
                  >
                    <option value="claimed_at">{lang === 'en' ? 'Sort: Claim Date' : 'Urut: Tgl Klaim'}</option>
                    <option value="created_at">{lang === 'en' ? 'Sort: Join Date' : 'Urut: Tgl Bergabung'}</option>
                    <option value="name">{lang === 'en' ? 'Sort: Name' : 'Urut: Nama'}</option>
                    <option value="whatsapp">{lang === 'en' ? 'Sort: WhatsApp' : 'Urut: WhatsApp'}</option>
                  </select>
                  <button
                    data-testid="admin-accounts-sort-order"
                    onClick={() => setAccountSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
                    className="t-bg-input t-border h-10 px-3 rounded-lg text-sm t-text hover:bg-[#C7867A]/10 transition"
                  >
                    {accountSortOrder === 'asc' ? (lang === 'en' ? '↑ Asc' : '↑ Naik') : (lang === 'en' ? '↓ Desc' : '↓ Turun')}
                  </button>
                  {(accountDebouncedQuery || accountClaimStatus !== 'all' || accountClaimFrom || accountClaimTo || accountSortBy !== 'claimed_at' || accountSortOrder !== 'desc') && (
                    <button
                      data-testid="admin-accounts-clear-filters"
                      onClick={clearAccountFilters}
                      className="h-10 px-3 rounded-lg bg-[#D98A94]/10 text-[#D98A94] text-xs font-semibold flex items-center gap-1"
                    >
                      <X className="w-3.5 h-3.5" /> {lang === 'en' ? 'Clear' : 'Hapus'}
                    </button>
                  )}
                  <span className="text-xs t-text-muted whitespace-nowrap ml-auto">
                    {lang === 'en'
                      ? `${totalAccounts} account${totalAccounts === 1 ? '' : 's'}`
                      : `Total ${totalAccounts} akun`}
                  </span>
                </div>

                <AccountsTable accounts={accounts} lang={lang} t={t} onUnclaim={setUnclaimAccountTarget} />

                {/* Pagination */}
                {totalAccounts > ACCOUNTS_PAGE_SIZE && (
                  <div className="mt-4 flex items-center justify-center gap-2" data-testid="accounts-pagination">
                    <button
                      onClick={() => setAccountPage(p => Math.max(1, p - 1))}
                      disabled={accountPage <= 1}
                      className="h-9 px-4 rounded-full glass-card t-text text-sm font-medium disabled:opacity-40 hover:bg-[#D98A94]/10 transition"
                      data-testid="accounts-pagination-prev"
                    >
                      {lang === 'en' ? '‹ Prev' : '‹ Sebelumnya'}
                    </button>
                    <span className="text-xs t-text-muted px-2">
                      {lang === 'en'
                        ? `Page ${accountPage} of ${Math.ceil(totalAccounts / ACCOUNTS_PAGE_SIZE)}`
                        : `Halaman ${accountPage} dari ${Math.ceil(totalAccounts / ACCOUNTS_PAGE_SIZE)}`}
                    </span>
                    <button
                      onClick={() => setAccountPage(p => p + 1)}
                      disabled={accountPage >= Math.ceil(totalAccounts / ACCOUNTS_PAGE_SIZE)}
                      className="h-9 px-4 rounded-full glass-card t-text text-sm font-medium disabled:opacity-40 hover:bg-[#D98A94]/10 transition"
                      data-testid="accounts-pagination-next"
                    >
                      {lang === 'en' ? 'Next ›' : 'Selanjutnya ›'}
                    </button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="ai-matches">
                <PotentialMatchesTab />
              </TabsContent>

              <TabsContent value="tickets">
                <AdminTicketsTab />
              </TabsContent>

              <TabsContent value="activity">
                <AdminPresenceTab />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      {/* Create Member Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="glass-card t-text max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="t-text">{t('create_member')}</DialogTitle>
            <DialogDescription className="t-text-muted text-xs">
              {lang === 'en' ? 'Fill in member details. Required: gender, age, category, domicile, whatsapp.' : 'Isi detail anggota. Wajib: gender, usia, kategori, domisili, whatsapp.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Photos */}
            <div className="grid grid-cols-3 gap-3">
              {[0, 1, 2].map(i => (
                <div key={i}>
                  <button onClick={() => openCropForCreate(i)} data-testid={`create-photo-${i}`}
                    className={`w-full aspect-[3/4] rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden ${createPhotos[i] ? 'border-[#D98A94]/50' : 'border-[var(--border-color)]'}`}>
                    {createPhotos[i] ? <img src={createPhotos[i]} alt="" className="w-full h-full object-cover" /> : <Camera className="w-5 h-5 t-text-muted" />}
                  </button>
                  <p className="text-[10px] t-text-muted text-center mt-1">{i === 0 ? t('primary_photo') : t('optional_photo')}</p>
                </div>
              ))}
            </div>

            <MemberFormFields form={createForm} setForm={setCreateForm} mode="create" lang={lang} t={t} />

            <div className="flex gap-3">
              <Button onClick={() => setShowCreate(false)} variant="outline" className="flex-1 rounded-lg t-border t-text-secondary" data-testid="create-cancel-btn">{t('cancel')}</Button>
              <Button onClick={handleCreate} className="flex-1 bg-[#C7867A] hover:bg-[#C7867A]/90 text-white rounded-lg" data-testid="create-submit-btn">{t('save')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={!!editMember} onOpenChange={() => setEditMember(null)}>
        <DialogContent className="glass-card t-text max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="t-text">{t('edit')} {editMember?.name || 'Member'}</DialogTitle>
            <DialogDescription className="t-text-muted text-xs">
              {lang === 'en' ? 'Update the member profile details below.' : 'Perbarui detail profil anggota di bawah.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {/* Edit photos */}
            {editMember && (
              <div className="grid grid-cols-3 gap-3 mb-2">
                {[0, 1, 2].map(i => (
                  <div key={i}>
                    <button type="button" onClick={() => openCropForEdit(i)} className={`w-full aspect-[3/4] rounded-xl border-2 border-dashed cursor-pointer overflow-hidden ${editMember.photos?.[i] ? 'border-[#D98A94]/50' : 'border-[var(--border-color)]'}`}>
                      {editMember.photos?.[i] ? <img src={editMember.photos[i]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Camera className="w-5 h-5 t-text-muted" /></div>}
                    </button>
                    <p className="text-[10px] t-text-muted text-center mt-1">{i === 0 ? (lang === 'en' ? 'Primary' : 'Utama') : (lang === 'en' ? 'Optional' : 'Opsional')}</p>
                  </div>
                ))}
              </div>
            )}
            <MemberFormFields form={editForm} setForm={setEditForm} mode="edit" lang={lang} t={t} />
            <div className="flex gap-3 mt-4">
              <Button onClick={() => setEditMember(null)} variant="outline" className="flex-1 t-border t-text-secondary rounded-lg" data-testid="dialog-cancel-btn">{t('cancel')}</Button>
              <Button onClick={handleSaveEdit} className="flex-1 bg-[#C7867A] hover:bg-[#C7867A]/90 text-white rounded-lg" data-testid="dialog-save-btn">{t('save')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="glass-card t-text max-w-sm">
          <DialogHeader>
            <DialogTitle className="t-text flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              {lang === 'en' ? 'Delete Member' : 'Hapus Anggota'}
            </DialogTitle>
            <DialogDescription className="t-text-muted text-sm pt-2">
              {lang === 'en'
                ? `Are you sure you want to delete ${deleteTarget?.name || 'this member'}? This action cannot be undone.`
                : `Apakah Anda yakin ingin menghapus ${deleteTarget?.name || 'anggota ini'}? Tindakan ini tidak dapat dibatalkan.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button onClick={() => setDeleteTarget(null)} variant="outline" className="flex-1 t-border t-text-secondary rounded-lg" data-testid="delete-cancel-btn" disabled={deleting}>
              {lang === 'en' ? 'Cancel' : 'Batal'}
            </Button>
            <Button onClick={handleDelete} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg" data-testid="delete-confirm-btn" disabled={deleting}>
              {deleting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : (lang === 'en' ? 'Delete' : 'Hapus')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unclaim (Reset Claim) Confirmation Dialog */}
      <Dialog open={!!unclaimTarget} onOpenChange={(open) => { if (!open) setUnclaimTarget(null); }}>
        <DialogContent className="glass-card t-text max-w-sm" data-testid="unclaim-dialog">
          <DialogHeader>
            <DialogTitle className="t-text flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
              {lang === 'en' ? 'Reset Claim' : 'Reset Klaim'}
            </DialogTitle>
            <DialogDescription className="t-text-muted text-sm pt-2">
              {lang === 'en' ? (
                <>
                  Reset the claim on <span className="t-text font-semibold">{unclaimTarget?.name || 'this profile'}</span>?
                  <br /><br />
                  Currently claimed by:{' '}
                  <span className="t-text font-mono text-xs">{unclaimTarget?.claimed_by?.whatsapp || unclaimTarget?.claimed_by?.email || '-'}</span>
                  <br />
                  This account will no longer own the profile and it will become available for another member to claim.
                </>
              ) : (
                <>
                  Reset klaim atas <span className="t-text font-semibold">{unclaimTarget?.name || 'profil ini'}</span>?
                  <br /><br />
                  Diklaim oleh:{' '}
                  <span className="t-text font-mono text-xs">{unclaimTarget?.claimed_by?.whatsapp || unclaimTarget?.claimed_by?.email || '-'}</span>
                  <br />
                  Akun tersebut tidak lagi memiliki profil ini dan profil akan tersedia untuk diklaim ulang oleh anggota lain.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button onClick={() => setUnclaimTarget(null)} variant="outline" className="flex-1 t-border t-text-secondary rounded-lg" data-testid="unclaim-cancel-btn" disabled={unclaiming}>
              {lang === 'en' ? 'Cancel' : 'Batal'}
            </Button>
            <Button onClick={handleUnclaim} className="flex-1 bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-black rounded-lg font-semibold" data-testid="unclaim-confirm-btn" disabled={unclaiming}>
              {unclaiming ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : (lang === 'en' ? 'Reset Claim' : 'Reset Klaim')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unclaim (Account-level) Confirmation Dialog — used from the Akun Anggota tab */}
      <Dialog open={!!unclaimAccountTarget} onOpenChange={(open) => { if (!open) setUnclaimAccountTarget(null); }}>
        <DialogContent className="glass-card t-text max-w-sm" data-testid="unclaim-account-dialog">
          <DialogHeader>
            <DialogTitle className="t-text flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
              {lang === 'en' ? 'Reset Wrong Claim' : 'Reset Klaim yang Salah'}
            </DialogTitle>
            <DialogDescription className="t-text-muted text-sm pt-2">
              {lang === 'en' ? (
                <>
                  Account <span className="t-text font-semibold font-mono text-xs">{unclaimAccountTarget?.whatsapp || unclaimAccountTarget?.email}</span> currently claims profile{' '}
                  <span className="t-text font-semibold">{unclaimAccountTarget?.claimed_profile?.name || '-'}</span>.
                  <br /><br />
                  Resetting will free the profile so the correct member can claim it. The account itself remains active and can claim another profile later.
                </>
              ) : (
                <>
                  Akun <span className="t-text font-semibold font-mono text-xs">{unclaimAccountTarget?.whatsapp || unclaimAccountTarget?.email}</span> saat ini mengklaim profil{' '}
                  <span className="t-text font-semibold">{unclaimAccountTarget?.claimed_profile?.name || '-'}</span>.
                  <br /><br />
                  Reset klaim akan membebaskan profil ini agar bisa diklaim ulang oleh anggota yang benar. Akun tetap aktif dan bisa klaim profil lain nanti.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button onClick={() => setUnclaimAccountTarget(null)} variant="outline" className="flex-1 t-border t-text-secondary rounded-lg" data-testid="unclaim-account-cancel-btn" disabled={unclaimingAccount}>
              {lang === 'en' ? 'Cancel' : 'Batal'}
            </Button>
            <Button onClick={handleUnclaimAccount} className="flex-1 bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-black rounded-lg font-semibold" data-testid="unclaim-account-confirm-btn" disabled={unclaimingAccount}>
              {unclaimingAccount ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : (lang === 'en' ? 'Reset Claim' : 'Reset Klaim')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <ImageCropper open={cropOpen} onClose={() => setCropOpen(false)} onCropComplete={handleCropComplete} lang={lang} />

      {/* Category Explorer */}
      <Dialog open={!!selectedCatDetail} onOpenChange={(o) => { if (!o) setSelectedCatDetail(null); }}>
        <DialogContent className="glass-card t-text max-w-md">
          {selectedCatDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedCatDetail.color }} />
                  <span className="t-text">{selectedCatDetail.name}</span>
                </DialogTitle>
                <DialogDescription className="t-text-muted text-xs sr-only">
                  {lang === 'en' ? 'Category details' : 'Detail kategori'}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                <p className="t-text-secondary text-sm leading-relaxed">
                  {lang === 'en' ? selectedCatDetail.detail_en : selectedCatDetail.detail_id}
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
